/**
 * How old a stock snapshot is, said plainly.
 *
 * A balance figure carries a date and no clock: it is what SQL Accounting believed at the close of
 * that business day. Printing the number without the date invites it to be read as "now", and on
 * 2026-09-08 that cost a round of "your system is wrong" — the figures were right for the 6th,
 * two days stale because the nightly refresh had silently not fired.
 *
 * So the age is stated, and anything past a day is flagged. Stock genuinely moves overnight.
 */
export type SnapshotAge = {
  days: number;
  /** Past this, a reader must not assume the figure reflects today. */
  stale: boolean;
  label: string;
};

export function snapshotAge(snapshotDate: string | null, today = new Date()): SnapshotAge | null {
  if (snapshotDate === null || snapshotDate === '') return null;
  const snap = new Date(`${snapshotDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(snap.getTime())) return null;
  const now = new Date(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
  const days = Math.round((now.getTime() - snap.getTime()) / 86_400_000);

  // Negative would mean a snapshot dated in the future — a clock problem, not a fresh figure.
  if (days < 0) return { days, stale: true, label: 'dated in the future — check the clock' };
  if (days === 0) return { days, stale: false, label: 'today' };
  if (days === 1) return { days, stale: false, label: 'yesterday' };
  return { days, stale: true, label: `${days} days old` };
}
